import { Empty } from 'antd';
import * as CardList from 'components/UI/Responsive/CardList';
import { useEndOfList } from 'hooks/layout/useEndOfList';
import { Order } from 'model/Order';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { MyOrderDetailSheet } from '../Detail/MyOrderDetail';
import MyOrderCard, { MyOrderCardSkeleton } from './MyOrderCard';

import * as S from './styles';

interface Props {
  orders: Order[];
  totalCount: number;
  isLoading: boolean;
  hasSearched: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

type OrderDay = { date: string; label: string; orders: Order[] };

const groupOrdersByDay = (orders: Order[]): OrderDay[] =>
  orders.reduce<OrderDay[]>((days, order) => {
    const date = moment(order.saleConclusion).format('YYYY-MM-DD');
    const current = days.find((day) => day.date === date);
    if (current) {
      current.orders.push(order);
      return days;
    }
    return [
      ...days,
      {
        date,
        label: moment(order.saleConclusion).format('DD/MM/YYYY'),
        orders: [order],
      },
    ];
  }, []);

const renderPlaceholders = (count: number, label: string) => (
  <CardList.Cards role="status" aria-label={label}>
    {Array.from({ length: count }, (_, index) => (
      <MyOrderCardSkeleton key={index} />
    ))}
  </CardList.Cards>
);

export default function MyOrdersCardList({
  orders,
  totalCount,
  isLoading,
  hasSearched,
  hasMore,
  onLoadMore,
}: Props) {
  const [detailedOrder, setDetailedOrder] = useState<Order | null>(null);
  const days = useMemo(() => groupOrdersByDay(orders), [orders]);
  const isLoadingNext = isLoading && orders.length > 0;

  const loadMoreWhenIdle = useCallback(() => {
    if (hasMore && !isLoading) onLoadMore();
  }, [hasMore, isLoading, onLoadMore]);
  const endOfListRef = useEndOfList(loadMoreWhenIdle, orders.length);

  if (isLoading && !isLoadingNext) {
    return renderPlaceholders(4, 'Carregando pedidos');
  }

  if (orders.length === 0) {
    return (
      <Empty
        description={
          hasSearched
            ? 'Nenhum pedido encontrado'
            : 'Use os filtros para buscar seus pedidos'
        }
      />
    );
  }

  return (
    <>
      <CardList.List>
        <CardList.Toolbar>
          <CardList.Count>
            {totalCount.toLocaleString('pt-BR')} itens
          </CardList.Count>
        </CardList.Toolbar>
        {days.map((day) => (
          <CardList.Day key={day.date} aria-label={day.label}>
            <CardList.DayLabel>{day.label}</CardList.DayLabel>
            <CardList.Cards aria-label={`Pedidos de ${day.label}`}>
              {day.orders.map((order) => (
                <MyOrderCard
                  key={`${order.key}-${order.orderNumber}`}
                  order={order}
                  onClick={() => setDetailedOrder(order)}
                />
              ))}
            </CardList.Cards>
          </CardList.Day>
        ))}
        {isLoadingNext && renderPlaceholders(2, 'Carregando mais pedidos')}
        {!hasMore && <S.Hint>Todos os pedidos foram carregados</S.Hint>}
        <CardList.EndOfList ref={endOfListRef} />
      </CardList.List>
      <MyOrderDetailSheet
        order={detailedOrder}
        onClose={() => setDetailedOrder(null)}
      />
    </>
  );
}
